import { useEffect, useState } from 'react';

/**
 * Fetches the real product image (from our /api/products/image endpoint) and
 * returns it for use in `<img src>`. Uses IntersectionObserver so we don't
 * hit the backend until the container scrolls into view — that keeps IPRoyal
 * proxy bandwidth spend proportional to what users actually look at instead
 * of the raw catalog size.
 *
 * The lookup result is memoized in a module-scope Map for the session so
 * that pagination / re-renders don't re-fetch the same SKU. On page reload,
 * DynamoDB (server-side, 90-day TTL) still short-circuits the scrape path,
 * so the second visit is fast even in a fresh browser.
 *
 * Usage:
 *   const ref = useRef(null);
 *   const { imageUrl, loading } = useProductImage({
 *     partNumber: product.partNumber,
 *     brand: product.manufacturer,
 *     containerRef: ref,
 *   });
 *   return <div ref={ref}><img src={imageUrl || product.image} /></div>;
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

// Cross-component in-memory cache. Key = partNumber. Value = Promise resolving
// to { imageUrl } (or null when the backend confirmed there's no image).
const inFlight = new Map();

function fetchImage(partNumber, brand, upc, name, ingramPartNumber) {
  const key = partNumber;
  if (inFlight.has(key)) return inFlight.get(key);

  // Identifiers sent to the scraper cascade:
  //   ingramPartNumber → primary lookup on Ingram's own portal (highest signal)
  //   upc              → Icecat + UPCitemdb keyed lookup
  //   brand + name     → retailer-search variants
  const params = new URLSearchParams();
  if (brand) params.set('brand', brand);
  if (upc) params.set('upc', upc);
  if (name) params.set('name', name);
  if (ingramPartNumber) params.set('ingramPartNumber', ingramPartNumber);
  const url = `${API_BASE_URL}/products/image/${encodeURIComponent(partNumber)}?${params.toString()}`;

  const promise = fetch(url)
    .then((r) => (r.ok ? r.json() : { imageUrl: null }))
    .then((res) => ({ imageUrl: res?.imageUrl || null }))
    .catch(() => ({ imageUrl: null }));

  inFlight.set(key, promise);
  // Don't poison the cache on transient failure. If the lookup came back
  // with no image (network error, backend hiccup, retailer momentarily
  // blocked us), remove it from the map so the next remount retries.
  // Successful hits stay cached for the session.
  promise.then((res) => { if (!res?.imageUrl) inFlight.delete(key); });
  return promise;
}

export function useProductImage({ partNumber, brand, upc, name, ingramPartNumber, initialImageUrl, containerRef }) {
  const [state, setState] = useState({ imageUrl: initialImageUrl || null, loading: false });

  useEffect(() => {
    // Fast-path: the /search response already carried a cached imageUrl
    // (single-wave load) — use it, skip the per-card round-trip entirely.
    if (initialImageUrl) {
      setState({ imageUrl: initialImageUrl, loading: false });
      return undefined;
    }
    if (!partNumber || !containerRef?.current) return undefined;

    // If we already have a resolved cache entry, use it immediately —
    // no need for IntersectionObserver at all.
    // (Map stores Promises; wrap with Promise.resolve so we always await.)
    let cancelled = false;

    const handleFetch = () => {
      setState((s) => ({ ...s, loading: true }));
      fetchImage(partNumber, brand, upc, name, ingramPartNumber).then((res) => {
        if (!cancelled) {
          setState({ imageUrl: res.imageUrl || null, loading: false });
        }
      });
    };

    if (inFlight.has(partNumber)) {
      // Already fetched (or in flight) — resolve without waiting for scroll.
      handleFetch();
      return () => {
        cancelled = true;
      };
    }

    // Fresh lookup. rootMargin 2000px (~4 viewport heights) makes cards on
    // typical catalog pages fetch immediately on first paint — the whole
    // first-few-pages of the grid falls within that range. Only enormous
    // pages get true lazy behavior for cards far below the fold. This
    // trades a small bandwidth increase for the "images always show up"
    // promise the UX needs.
    const io = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        io.disconnect();
        handleFetch();
      },
      { rootMargin: '2000px' },
    );

    io.observe(containerRef.current);
    return () => {
      cancelled = true;
      io.disconnect();
    };
  }, [partNumber, brand, upc, name, ingramPartNumber, initialImageUrl, containerRef]);

  return state;
}

export default useProductImage;
